import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationsStore } from '@/stores/organizations';
const routes = [
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { requiresGuest: true, title: 'Login - YGP' }
    },
    {
        path: '/register',
        name: 'Register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: { requiresGuest: true, title: 'Register - YGP' }
    },
    {
        path: '/',
        component: () => import('@/layouts/DashboardLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'Dashboard',
                component: () => import('@/views/DashboardView.vue')
            },
            {
                path: '/employees',
                name: 'Employees',
                component: () => import('@/views/employees/EmployeeListView.vue')
            },
            {
                path: '/employees/create',
                name: 'CreateEmployee',
                component: () => import('@/views/employees/CreateEmployeeView.vue')
            },
            {
                path: '/employees/:id',
                name: 'EmployeeDetail',
                component: () => import('@/views/employees/EmployeeDetailView.vue'),
                props: true
            },
            {
                path: '/employees/:id/edit',
                name: 'EditEmployee',
                component: () => import('@/views/employees/EditEmployeeView.vue'),
                props: true
            },
            {
                path: '/campaigns',
                name: 'Campaigns',
                component: () => import('@/views/campaigns/CampaignListView.vue')
            },
            {
                path: '/campaigns/create',
                name: 'CreateCampaign',
                component: () => import('@/views/campaigns/CreateCampaignView.vue')
            },
            {
                path: '/campaigns/:id',
                name: 'CampaignDetail',
                component: () => import('@/views/campaigns/CampaignDetailView.vue'),
                props: true
            },
            {
                path: '/campaigns/:id/edit',
                name: 'EditCampaign',
                component: () => import('@/views/campaigns/EditCampaignView.vue'),
                props: true
            },
            {
                path: '/templates',
                name: 'Templates',
                component: () => import('@/views/templates/TemplateListView.vue')
            },
            {
                path: '/templates/create',
                name: 'CreateTemplate',
                component: () => import('@/views/templates/CreateTemplateView.vue')
            },
            {
                path: '/templates/:id',
                name: 'TemplateDetail',
                component: () => import('@/views/templates/TemplateDetailView.vue'),
                props: true
            },
            {
                path: '/templates/:id/edit',
                name: 'EditTemplate',
                component: () => import('@/views/templates/EditTemplateView.vue'),
                props: true
            },
            {
                path: '/analytics',
                name: 'Analytics',
                component: () => import('@/views/AnalyticsView.vue')
            },
            {
                path: '/reports',
                name: 'Reports',
                component: () => import('@/views/reports/CampaignReportView.vue')
            },
            {
                path: '/domains',
                name: 'Domains',
                component: () => import('@/views/domains/DomainsView.vue')
            },
            {
                path: '/training',
                name: 'Training',
                component: () => import('@/views/training/TrainingDashboardView.vue')
            },
            {
                path: '/training/portal/:token',
                name: 'TrainingPortal',
                component: () => import('@/views/training/EmployeeTrainingPortalView.vue'),
                props: true
            },
            {
                path: '/training/content',
                name: 'TrainingContent',
                component: () => import('@/views/training/TrainingContentView.vue')
            },
            {
                path: '/settings',
                name: 'Settings',
                component: () => import('@/views/settings/SettingsView.vue')
            },
            {
                path: '/settings/email',
                name: 'SystemEmail',
                component: () => import('@/views/settings/SystemEmailView.vue')
            },
        ]
    },
    // Public training session route (no authentication required)
    {
        path: '/training/session/:token',
        name: 'TrainingSession',
        component: () => import('@/views/training/TrainingPortalTokenView.vue'),
        props: true,
        meta: { title: 'Training Portal - YGP' }
    },
    // Public employee portal route (no authentication required)
    {
        path: '/training/:token',
        name: 'EmployeePortal',
        component: () => import('@/views/training/EmployeeTrainingPortalView.vue'),
        props: true,
        meta: { title: 'Employee Training Portal - YGP' }
    },
    // Portal token route for "View Portal" functionality
    {
        path: '/training/portal/:token',
        name: 'EmployeePortalToken',
        component: () => import('@/views/training/EmployeeTrainingPortalView.vue'),
        props: true,
        meta: { title: 'Employee Training Portal - YGP' }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/NotFoundView.vue')
    }
];
const router = createRouter({
    history: createWebHistory(),
    routes
});
// Navigation guards
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const organizationsStore = useOrganizationsStore();
    // Set document title based on route metadata
    if (to.meta.title) {
        document.title = to.meta.title;
    }
    else {
        document.title = 'YGP - You Got Phished';
    }
    // Authentication is now properly implemented
    // Users must login to access protected routes
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        next('/login');
    }
    else if (to.meta.requiresGuest && authStore.isAuthenticated) {
        next('/');
    }
    else {
        // If authenticated and navigating to protected routes, ensure organizations are loaded
        if (authStore.isAuthenticated && to.meta.requiresAuth) {
            if (organizationsStore.organizations.length === 0) {
                try {
                    await organizationsStore.fetchOrganizations();
                }
                catch (error) {
                    console.error('Failed to load organizations:', error);
                }
            }
        }
        next();
    }
});
export default router;
